import { useContext, useMemo, type FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { sanitizeHref } from '@wix/da-url';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { getModelPropertiesByType } from '../../ModelProperties';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import VideoSound from '@wix/da-ds/pkg/Icons/12x12/VideoSound';
import CopyIcon from '@wix/da-ds/pkg/Icons/16x16/Copy';
import { useCopyToClipboard } from '@wix/da-shared-react/pkg/utils/hooks/useCopyToClipboard';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { VideoCreatorPendingGeneration } from '../../../../types/store';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useIsNarrowViewport } from '../../../../hooks/useIsNarrowViewport';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import {
  BiData,
  VideoCreatorVideoActionClickBiEvent,
} from '@wix/da-bi/pkg/events';

import s from './VideoPendingGeneration.scss';
import sVideoFile from '../VideoFile/VideoFile.scss';
import sParent from '../VideoCreatorFiles.scss';

export interface Props {
  pendingGeneration: VideoCreatorPendingGeneration;
}

const VideoPendingGeneration: FC<Props> = ({ pendingGeneration }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isNarrow = useIsNarrowViewport();

  const referenceDeviation = pendingGeneration.referenceDeviation;
  const referenceDeviationThumb = referenceDeviation
    ? DeviationMediaService.getSmallestThumb(referenceDeviation)
    : undefined;

  const model = useMemo(() => {
    return getModelPropertiesByType(pendingGeneration.modelType);
  }, [pendingGeneration.modelType]);

  const copyPromptToClipboard = useCopyToClipboard(
    pendingGeneration.prompt || ''
  );

  return (
    <div
      className={cn(
        s['root'],
        sParent['file-item'],
        isNarrow && sVideoFile['narrow'],
        isMobile && s['mobile'],
        isMobile && sVideoFile['mobile']
      )}
    >
      <div className={sVideoFile['preview']}>
        <div className={s['llama-part']}>
          <div className={s['llama-icon']}>
            <LoadingIndicator view="llama" className={s['llama-loader']} />
          </div>
          <div className={s['llama-text']}>
            {t('studio.videoCreator.results.loading')}
          </div>
        </div>
      </div>
      <div className={s['details']}>
        {referenceDeviationThumb && (
          <div
            className={cn(sVideoFile['reference-thumb'], s['reference-thumb'])}
          >
            <BiLink
              href={sanitizeHref(referenceDeviation.url)}
              target="_blank"
              rel="noopener noreferrer"
              biData={BiData<VideoCreatorVideoActionClickBiEvent>({
                evid: 951,
                generationid: pendingGeneration.generationId,
                action: 'starting_image_thumb',
                is_valid: true,
              })}
            >
              <img
                className={sVideoFile['thumb-image']}
                src={referenceDeviationThumb.src}
                alt={referenceDeviation.title ?? ''}
                title={referenceDeviation.title ?? ''}
              />
            </BiLink>
          </div>
        )}
        <div
          className={cn(
            sVideoFile['prompt'],
            s['prompt'],
            referenceDeviationThumb && sVideoFile['has-reference-thumb']
          )}
        >
          <span className={sVideoFile['prompt-text']}>
            {pendingGeneration.prompt}
          </span>
          <IconButton
            className={sVideoFile['copy-icon']}
            icon={CopyIcon}
            size={'small'}
            title={t('studio.videoCreator.files.copy')}
            aria-label={t('studio.videoCreator.files.copy')}
            onClick={copyPromptToClipboard}
            biData={BiData<VideoCreatorVideoActionClickBiEvent>({
              evid: 951,
              generationid: pendingGeneration.generationId,
              action: 'prompt_copy',
              is_valid: true,
            })}
          />
        </div>
        <div className={sVideoFile['properties']}>
          {model && (
            <div
              className={cn(sVideoFile['model-name'], sVideoFile['property'])}
            >
              {t(`studio.videoCreator.modelName.${model.type}`)}
            </div>
          )}
          <div
            className={cn(sVideoFile['aspect-ratio'], sVideoFile['property'])}
          >
            {pendingGeneration.aspectRatio}
          </div>
          <div className={cn(sVideoFile['duration'], sVideoFile['property'])}>
            {pendingGeneration.duration}s
          </div>
          {pendingGeneration.sound && (
            <div className={cn(sVideoFile['sound'], sVideoFile['property'])}>
              <IconButton
                icon={VideoSound}
                size={'small'}
                title={t('studio.videoCreator.label.sound')}
                aria-label={t('studio.videoCreator.label.sound')}
                className={sVideoFile['sound-icon']}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

VideoPendingGeneration.displayName = 'VideoPendingGeneration';
export default VideoPendingGeneration;
